// The annual check-in's ten questions and their pure rules (Marketing Hub
// row 3.7, build spec screen 47, plan section 4.10).
//
// CONSUMER: both (the public check-in page draws the questions from here;
// the save in shared/db/marketing-checkin.ts turns a parsed submission into
// writes; the household Marketing tab prints "In their words").
//
// WHERE EACH ANSWER LANDS (one home per fact; the spec's element table):
//   1 household change, 2 work change, 3 moving    kept with the check-in only
//   4 life insurance                               the service map (Planning, life_case)
//   5 umbrella                                     the service map (Insurance, umbrella_policy)
//   6 topics                                       the person's marketing preferences
//   7 what to watch for                            kept with the check-in, their words
//   8 across the five companies                    the person's affiliate record
//   9 how often                                    the person's marketing preferences
//   10 anyone we should help                       a referral, and their words
//
// Every question can be skipped ("Skip anything"); a skipped question is
// absent from the submission and changes nothing. Only two answers are free
// text (7 and 10), each capped; everything else is checked against a list,
// so nothing a stranger types reaches a column it was not meant for.
//
// Hard rule: PURE. No database, no React.

import { CADENCES, type AffiliateChoice, type Cadence } from "./preferences";
import type { ServiceMapStoredState } from "./service-map";

export interface ChoiceOption<K extends string = string> {
  key: K;
  label: string;
}

export const HOUSEHOLD_CHANGE = [
  { key: "no_change", label: "No change" },
  { key: "someone_joined", label: "Someone joined" },
  { key: "someone_left", label: "Someone left" },
] as const satisfies readonly ChoiceOption[];

export const WORK_CHANGE = [
  { key: "no", label: "No" },
  { key: "job_change", label: "Job change" },
  { key: "started_business", label: "Started a business" },
  { key: "retired", label: "Retired" },
] as const satisfies readonly ChoiceOption[];

export const MOVING = [
  { key: "no", label: "No" },
  { key: "maybe", label: "Maybe" },
  { key: "within_a_year", label: "Yes, within a year" },
] as const satisfies readonly ChoiceOption[];

export const LIFE_INSURANCE = [
  { key: "through_work", label: "Through work" },
  { key: "own_policy", label: "Our own policy" },
  { key: "no", label: "No" },
  { key: "not_sure", label: "Not sure" },
] as const satisfies readonly ChoiceOption[];

export const UMBRELLA = [
  { key: "yes", label: "Yes" },
  { key: "no", label: "No" },
  { key: "not_sure", label: "Not sure what that is" },
] as const satisfies readonly ChoiceOption[];

/**
 * The topics question shows five parts of life in the family's own words,
 * each one a key from the preference center's list (shared/marketing/
 * topics.ts). Answering it changes only these five on the person's
 * preferences: a topic they chose on the preference center that is not
 * offered here (taxes, say) is left exactly as it was.
 */
export const CHECKIN_TOPICS = [
  { key: "owning_a_home", label: "Owning our home" },
  { key: "protecting_what_you_own", label: "Protecting the family" },
  { key: "saving_and_investing", label: "Investing" },
  { key: "family_future", label: "Planning and estate" },
  { key: "health_and_medicare", label: "Medicare" },
] as const satisfies readonly ChoiceOption[];

/** Question 9 offers three of the preference center's four cadences. */
export const CHECKIN_CADENCES = CADENCES.filter((c) => c.key !== "only_what_i_use").map((c) => ({
  key: c.key,
  label: c.label,
})) as readonly ChoiceOption<Cadence>[];

export const CHECKIN_AFFILIATE = [
  { key: "one_team", label: "Yes, that's why we chose one team" },
  { key: "only_company_asked", label: "Only the company I asked" },
] as const satisfies readonly ChoiceOption<AffiliateChoice>[];
// The drawing's question 8 words differ from the preference center's; the
// keys are the same two, which the `satisfies` above holds the compiler to.

export const WORDS_MAX = 500;
export const NAME_MAX = 80;
export const CONTACT_MAX = 120;
/** At most this many people from one person's check-ins in one year. */
export const MAX_INTRODUCTIONS_PER_YEAR = 3;

export type QuestionKind = "one" | "many" | "words" | "introduce";

export interface CheckinQuestion {
  key: keyof CheckinAnswers;
  number: number;
  prompt: string;
  kind: QuestionKind;
  options?: readonly ChoiceOption[];
  placeholder?: string;
}

/** The ten, in the drawing's order and words. */
export const CHECKIN_QUESTIONS: readonly CheckinQuestion[] = [
  { key: "household_change", number: 1, prompt: "Has anyone joined or left your household this year?", kind: "one", options: HOUSEHOLD_CHANGE },
  { key: "work_change", number: 2, prompt: "Any job change, new business, or retirement?", kind: "one", options: WORK_CHANGE },
  { key: "moving", number: 3, prompt: "Are you thinking about moving in the next two years?", kind: "one", options: MOVING },
  { key: "life_insurance", number: 4, prompt: "Do you have life insurance anywhere?", kind: "one", options: LIFE_INSURANCE },
  { key: "umbrella", number: 5, prompt: "Do you have an umbrella policy anywhere?", kind: "one", options: UMBRELLA },
  { key: "topics", number: 6, prompt: "Which topics would actually be useful this year?", kind: "many", options: CHECKIN_TOPICS },
  { key: "watch_for", number: 7, prompt: "What should we keep an eye on for you?", kind: "words", placeholder: "College in a few years. Whether a bigger house makes sense." },
  { key: "cross_company", number: 8, prompt: "May the Stewardship companies use what you tell us here across all five?", kind: "one", options: CHECKIN_AFFILIATE },
  { key: "cadence", number: 9, prompt: "How often is right?", kind: "one", options: CHECKIN_CADENCES },
  { key: "introduce", number: 10, prompt: "Is there anyone we should help the way we've helped you?", kind: "introduce", placeholder: "Maybe my sister, she's renting in Mesa." },
];

// The value exchange is no longer a promise under the questions: since
// row 6.8 "Send my answers" opens the household review itself (decision
// 43 (4): the "What it's for" paragraph came off once the check-in rode
// the digest).

export interface CheckinView {
  /** The person the link names: their first name only. */
  first_name: string;
  /** Every adult in the household, first names only, for "Hi Maria and Tomás". */
  household_first_names: string[];
  year: number;
  /** The last day the link works (YYYY-MM-DD). */
  link_last_day: string;
  /** When this person last sent a check-in this year, or null. Their
   *  answers are never sent back to the page (nothing pre-filled). */
  already_sent_on: string | null;
}

export interface Introduction {
  name: string;
  /** A phone number or an email, as typed; optional. */
  reach: string | null;
}

/** A submission, every question optional. */
export interface CheckinAnswers {
  household_change?: (typeof HOUSEHOLD_CHANGE)[number]["key"];
  work_change?: (typeof WORK_CHANGE)[number]["key"];
  moving?: (typeof MOVING)[number]["key"];
  life_insurance?: (typeof LIFE_INSURANCE)[number]["key"];
  umbrella?: (typeof UMBRELLA)[number]["key"];
  topics?: string[];
  watch_for?: string;
  cross_company?: AffiliateChoice;
  cadence?: Cadence;
  introduce?: { words: string | null; people: Introduction[] };
}

export type ParsedCheckin = { ok: true; answers: CheckinAnswers } | { ok: false; message: string };

function pick<T extends readonly ChoiceOption[]>(options: T, value: unknown): T[number]["key"] | undefined | null {
  if (value === undefined || value === null || value === "") return undefined;
  return options.some((o) => o.key === value) ? (value as T[number]["key"]) : null;
}

function words(value: unknown, max: number): string | undefined | null {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== "string") return null;
  const t = value.replace(/\s+/g, " ").trim();
  if (t === "") return undefined;
  return t.length > max ? null : t;
}

/**
 * Parse what the page sent. An answer outside its list is refused, not
 * dropped, so a stale page never silently loses a choice. Empty means
 * skipped. Introductions without a name are dropped (the words still
 * stand); more than MAX_INTRODUCTIONS_PER_YEAR is refused.
 */
export function parseCheckinSubmission(body: unknown): ParsedCheckin {
  const raw = ((body ?? {}) as { answers?: unknown }).answers;
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return { ok: false, message: "Nothing came through. Try sending again." };
  const a = raw as Record<string, unknown>;
  const out: CheckinAnswers = {};
  const bad = { ok: false as const, message: "One answer is not on the list. Reload the page and try again." };

  const one = <T extends readonly ChoiceOption[]>(key: keyof CheckinAnswers, options: T): boolean => {
    const v = pick(options, a[key]);
    if (v === null) return false;
    if (v !== undefined) (out as Record<string, unknown>)[key] = v;
    return true;
  };
  if (!one("household_change", HOUSEHOLD_CHANGE)) return bad;
  if (!one("work_change", WORK_CHANGE)) return bad;
  if (!one("moving", MOVING)) return bad;
  if (!one("life_insurance", LIFE_INSURANCE)) return bad;
  if (!one("umbrella", UMBRELLA)) return bad;
  if (!one("cross_company", CHECKIN_AFFILIATE)) return bad;
  if (!one("cadence", CHECKIN_CADENCES)) return bad;

  if (a.topics !== undefined && a.topics !== null) {
    if (!Array.isArray(a.topics) || !a.topics.every((t) => CHECKIN_TOPICS.some((o) => o.key === t))) return bad;
    // De-duplicated, in the list's own order. An empty list is an answer:
    // "none of these" (it clears the five on their preferences).
    out.topics = CHECKIN_TOPICS.map((o) => o.key as string).filter((k) => (a.topics as unknown[]).includes(k));
  }

  const watch = words(a.watch_for, WORDS_MAX);
  if (watch === null) return { ok: false, message: `Question 7 is a little long. Keep it under ${WORDS_MAX} characters.` };
  if (watch !== undefined) out.watch_for = watch;

  if (a.introduce !== undefined && a.introduce !== null) {
    const intro = a.introduce as { words?: unknown; people?: unknown };
    if (typeof intro !== "object") return bad;
    const w = words(intro.words, WORDS_MAX);
    if (w === null) return { ok: false, message: `Question 10 is a little long. Keep it under ${WORDS_MAX} characters.` };
    const peopleIn = intro.people === undefined || intro.people === null ? [] : intro.people;
    if (!Array.isArray(peopleIn)) return bad;
    const people: Introduction[] = [];
    for (const p of peopleIn) {
      const person = (p ?? {}) as { name?: unknown; reach?: unknown };
      const name = words(person.name, NAME_MAX);
      if (name === null) return { ok: false, message: `A name is a little long. Keep it under ${NAME_MAX} characters.` };
      const reach = words(person.reach, CONTACT_MAX);
      if (reach === null) return { ok: false, message: "A phone number or email is a little long." };
      if (name) people.push({ name, reach: reach ?? null });
    }
    if (people.length > MAX_INTRODUCTIONS_PER_YEAR) {
      return { ok: false, message: `Up to ${MAX_INTRODUCTIONS_PER_YEAR} people at a time, please.` };
    }
    if (w !== undefined || people.length > 0) out.introduce = { words: w ?? null, people };
  }
  return { ok: true, answers: out };
}

/** True when nothing was answered at all. */
export function isEmptyCheckin(a: CheckinAnswers): boolean {
  return Object.keys(a).length === 0;
}

/**
 * What question 4 or 5 writes on the service map. Null means "write
 * nothing": "no" and "not sure" leave the cell as it is (unknown stays
 * unknown). A "no" is not "interested": the OS never decides a family
 * wants something because they do not have it (plan rule 4); the words are
 * kept with the check-in and the advisor reads them.
 */
export function mapAnswerFor(
  question: "life_insurance" | "umbrella",
  value: string,
): { state: ServiceMapStoredState; note: string } | null {
  if (question === "life_insurance") {
    if (value === "through_work") return { state: "has_it_elsewhere", note: "Through work (their check-in)" };
    if (value === "own_policy") return { state: "has_it_elsewhere", note: "Their own policy (their check-in)" };
    return null;
  }
  if (value === "yes") return { state: "has_it_elsewhere", note: "Has one (their check-in)" };
  return null;
}

/** The service map cell each of questions 4 and 5 fills. */
export const CHECKIN_MAP_CELLS = {
  life_insurance: { companySlug: "planning", serviceKey: "life_case" },
  umbrella: { companySlug: "insurance", serviceKey: "umbrella_policy" },
} as const;

/**
 * The topics after question 6: the person's other topics kept, the five
 * offered here replaced by what they chose.
 */
export function mergeCheckinTopics(existing: readonly string[], chosen: readonly string[]): string[] {
  const offered = new Set<string>(CHECKIN_TOPICS.map((t) => t.key));
  const kept = existing.filter((k) => !offered.has(k));
  return [...new Set([...kept, ...chosen])];
}

/** "Maria Villarreal" -> first and last; one word is a first name. */
export function splitName(full: string): { first: string; last: string | null } {
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return { first: parts[0]!, last: null };
  return { first: parts.slice(0, -1).join(" "), last: parts[parts.length - 1]! };
}

/** A typed "phone or email" read as one or the other. */
export function readReach(reach: string | null): { email: string | null; phone: string | null } {
  if (!reach) return { email: null, phone: null };
  const t = reach.trim();
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) return { email: t.toLowerCase(), phone: null };
  const digits = t.replace(/\D/g, "");
  if (digits.length >= 10 && digits.length <= 11) return { email: null, phone: t };
  return { email: null, phone: null };
}

/** One line per answered question, for "In their words" on the Marketing
 *  tab: "Moving: Maybe", "Watch for: College for Sofia in 2029." */
export function checkinLines(a: CheckinAnswers): { question: string; answer: string }[] {
  const out: { question: string; answer: string }[] = [];
  const label = (options: readonly ChoiceOption[], key: string | undefined) => options.find((o) => o.key === key)?.label ?? null;
  const push = (question: string, answer: string | null) => {
    if (answer) out.push({ question, answer });
  };
  push("Household", label(HOUSEHOLD_CHANGE, a.household_change));
  push("Work", label(WORK_CHANGE, a.work_change));
  push("Moving in two years", label(MOVING, a.moving));
  push("Life insurance", label(LIFE_INSURANCE, a.life_insurance));
  push("Umbrella", label(UMBRELLA, a.umbrella));
  if (a.topics) push("Useful this year", a.topics.length ? a.topics.map((k) => label(CHECKIN_TOPICS, k) ?? k).join(", ") : "None of these");
  push("Watch for", a.watch_for ?? null);
  push("Across the five companies", label(CHECKIN_AFFILIATE, a.cross_company));
  push("How often", label(CHECKIN_CADENCES, a.cadence));
  if (a.introduce) {
    const names = a.introduce.people.map((p) => p.name).join(", ");
    push("Someone we could help", [a.introduce.words, names ? `Named: ${names}` : null].filter(Boolean).join(" · "));
  }
  return out;
}

/**
 * The tasks a check-in leaves a person. Wording resolves against
 * communication_templates by slug (automation-first rule 2; the migration
 * seeds both rows); the text here is the fallback only while a row is
 * missing. Subject is the task's title, body its description.
 *   {{household}} the introducing family · {{introducer}} who filled it in
 *   {{name}} the person they named · {{reach}} the phone or email they gave
 *   {{words}} what they typed · {{due}} the response clock's day
 */
export const CHECKIN_INTRODUCTION_TASK = {
  slug: "checkin-task-introduction",
  subject: "Introduction from {{household}}: {{name}}",
  body:
    "{{introducer}} named {{name}} on their annual check-in as someone we could help the way we helped them. In their words: \"{{words}}\". Reach: {{reach}}. Please reach out by {{due}}. Thank {{introducer}} with a note, never a gift.",
} as const;

export const CHECKIN_NO_OWNER_TASK = {
  slug: "checkin-task-introduction-no-owner",
  subject: "Introduction from {{household}} needs an owner: {{name}}",
  body:
    "{{introducer}} named {{name}} on their annual check-in. Their household has no advisor on file, so nobody owns the introduction yet. In their words: \"{{words}}\". Reach: {{reach}}. Find the right company and hand it over.",
} as const;

/**
 * The task when the person named on question 10 is already in the book
 * but cannot become a referral from here: they have no household, or the
 * only match is in the introducing family itself. Never a silent drop
 * (slice 3 audit, finding 8): the front desk decides what the match means.
 */
export const CHECKIN_EXISTING_PERSON_TASK = {
  slug: "checkin-task-introduction-existing-person",
  subject: "Introduction from {{household}} matched an existing person: {{name}}",
  body:
    "{{introducer}} named {{name}} on their annual check-in, and the name and contact details matched an existing person in the OS who has no household of their own (or is in the introducing family). No referral was opened. In their words: \"{{words}}\". Reach: {{reach}}. Decide whether this is a real introduction, and if so open the referral by hand.",
} as const;

/** Every check-in task wording, for the system-template list. */
export const CHECKIN_TASK_SLUGS = [CHECKIN_INTRODUCTION_TASK.slug, CHECKIN_NO_OWNER_TASK.slug, CHECKIN_EXISTING_PERSON_TASK.slug] as const;

/** "2026-12-06" -> "Dec 6, 2026", for the page and the copy toast. */
export function dayWords(isoDay: string): string {
  const t = Date.parse(`${isoDay}T12:00:00.000Z`);
  if (Number.isNaN(t)) return isoDay;
  return new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}
